import Hls from 'hls.js';
import type Config from 'hls.js';
import { type VideoHTMLAttributes, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { VideoPlayer } from '../video-player';

export type HlsVideoPlayerProperties = {
  hlsConfig?: HlsConfig;
  video: VideoProperties;
};

type HlsConfig = Config;

type VideoProperties = {
  src: string;
} & Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'>;

const HlsVideoPlayer = forwardRef<HTMLVideoElement, HlsVideoPlayerProperties>(
  ({ hlsConfig, video: { autoPlay, src, ...props } }, ref) => {
    const playerReference = useRef<HTMLVideoElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We want also set this
    useImperativeHandle(ref, () => playerReference.current!, [playerReference]);

    useEffect(() => {
      let hls: Hls | null | undefined;

      function _initPlayer() {
        if (hls !== undefined && hls !== null) {
          hls.destroy();
        }

        const newHls = new Hls({
          enableWorker: false,
          // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          ...hlsConfig,
        });

        if (playerReference.current !== null) {
          newHls.attachMedia(playerReference.current);
        }

        newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
          newHls.loadSource(src);

          newHls.on(Hls.Events.MANIFEST_PARSED, async () => {
            if (autoPlay && playerReference.current) {
              try {
                await playerReference.current.play();
              } catch {
                console.warn('Unable to autoplay prior to user interaction with the dom.');
              }
            }
          });
        });

        newHls.on(Hls.Events.ERROR, function (_, data) {
          if (data.fatal) {
            // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            switch (data.type) {
              case Hls.ErrorTypes.MEDIA_ERROR:
                newHls.recoverMediaError();
                break;
              case Hls.ErrorTypes.NETWORK_ERROR:
                newHls.startLoad();
                break;
              default:
                _initPlayer();
                break;
            }
          }
        });

        hls = newHls;
      }

      if (Hls.isSupported()) {
        _initPlayer();
      }

      return () => {
        if (hls !== null && hls !== undefined) {
          hls.destroy();
        }
      };
    }, [autoPlay, hlsConfig, playerReference, src]);

    // If Media Source is supported, use HLS.js to play video
    if (Hls.isSupported()) {
      return <VideoPlayer ref={playerReference} {...props} />;
    }

    // Fallback to using a regular video player if HLS is supported by default in the user's browser
    return <VideoPlayer autoPlay={autoPlay} ref={playerReference} src={src} {...props} />;
  },
);

export default HlsVideoPlayer;
